import { projectsApi } from '../api/projectsApi';
import { getBundledTemplateProjectData } from './bundledTemplateProjects';
import { PYTHON_API_TEMPLATE_SHARE_CODE, ROBOT_TEMPLATES } from './constants';
import { pickProjectVersionFields, type ProjectVersionFields } from './projectVersions';

type CachedTemplateV1 = {
  version: 1;
  shareCode: string;
  projectString: string;
  /** API `updated_at` value when available (best-effort). */
  updatedAt?: string;
  /** Client-side cache timestamp. */
  cachedAt: string;
};

type CachedTemplateV2 = {
  version: 2;
  shareCode: string;
  projectString: string;
  /** API `updated_at` value when available (best-effort). */
  updatedAt?: string;
  /** Client-side cache timestamp. */
  cachedAt: string;
  projectVersion?: ProjectVersionFields['version'];
  frameworkVersion?: ProjectVersionFields['frameworkVersion'];
};

type CachedTemplateProjectData = {
  projectString: string;
  apiMetadata: ProjectVersionFields;
};

const STORAGE_PREFIX = 'bps.template-cache.';

function storageKey(shareCode: string) {
  return `${STORAGE_PREFIX}${shareCode}`;
}

function pickVersionFieldsFromProjectString(projectString: string): ProjectVersionFields {
  try {
    return pickProjectVersionFields(JSON.parse(projectString));
  } catch {
    return {};
  }
}

export function getCachedTemplateProjectString(shareCode: string): string | null {
  return getCachedTemplateProjectData(shareCode)?.projectString ?? null;
}

export function getCachedTemplateProjectData(shareCode: string): CachedTemplateProjectData | null {
  try {
    const raw = localStorage.getItem(storageKey(shareCode));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedTemplateV1 | CachedTemplateV2>;
    if (parsed.version !== 1 && parsed.version !== 2) return null;
    if (typeof parsed.projectString !== 'string') return null;

    return {
      projectString: parsed.projectString,
      apiMetadata:
        parsed.version === 2
          ? {
              ...(parsed.projectVersion && { version: parsed.projectVersion }),
              ...(parsed.frameworkVersion && { frameworkVersion: parsed.frameworkVersion }),
            }
          : pickVersionFieldsFromProjectString(parsed.projectString),
    };
  } catch {
    return null;
  }
}

export function getBundledTemplateFallbackData(shareCode: string): CachedTemplateProjectData | null {
  return getBundledTemplateProjectData(shareCode);
}

export function cacheTemplateProjectString(shareCode: string, projectString: string, updatedAt?: string, apiMetadata?: unknown) {
  try {
    const versionFields = pickProjectVersionFields(apiMetadata);
    const payload: CachedTemplateV2 = {
      version: 2,
      shareCode,
      projectString,
      updatedAt,
      cachedAt: new Date().toISOString(),
      ...(versionFields.version && { projectVersion: versionFields.version }),
      ...(versionFields.frameworkVersion && { frameworkVersion: versionFields.frameworkVersion }),
    };
    localStorage.setItem(storageKey(shareCode), JSON.stringify(payload));
  } catch {
    // Best-effort caching only (ignore quota / disabled storage / JSON errors).
  }
}

export function getDefensiveTemplateShareCodes(): string[] {
  return Array.from(
    new Set(
      [PYTHON_API_TEMPLATE_SHARE_CODE, ...ROBOT_TEMPLATES.map((template) => template.shareCode)].filter(
        (c): c is string => typeof c === 'string' && c.length > 0
      )
    )
  );
}

export function isDefensiveTemplateShareCode(shareCode: string): boolean {
  return getDefensiveTemplateShareCodes().includes(shareCode);
}

/**
 * Best-effort prefetch for platform-backed starter templates we want available
 * offline/when the API is down. Rapid Robots reuse these same share codes.
 */
export async function prefetchDefensiveTemplates(): Promise<void> {
  const shareCodes = getDefensiveTemplateShareCodes();
  await Promise.all(
    shareCodes.map(async (code) => {
      try {
        const res = await projectsApi.get(code);
        cacheTemplateProjectString(code, res.project_string, res.updated_at, res);
      } catch {
        // ignore; this is defensive-only
      }
    })
  );
}
